/* eslint-disable react/destructuring-assignment */
import { DataGrid, frFR } from '@mui/x-data-grid';
import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { 
  Typography, 
  Link, 
  Tooltip, 
  Dialog, 
  Card, 
  DialogTitle, 
  Grid,
  DialogContentText, 
  DialogContent, 
  Button, 
  DialogActions, 
  TextField, 
  Autocomplete, 
  IconButton, 
  Stepper, 
  Step, 
  StepLabel,
  Box,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
} from "@material-ui/core";
import PropTypes from 'prop-types';
import { 
  Delete as DeleteIcon,
  Event as EventIcon,
  Timeline as TimelineIcon,
} from "@material-ui/icons";
import * as moment from 'moment';
import { sessionApi } from '../../../api/sessionApi';
import { customerApi } from '../../../api/customerApi';
import toast from 'react-hot-toast';
import useAuth from '../../../hooks/useAuth';
import useMounted from '../../../hooks/useMounted';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey } from '../../../theme/tones';
import usePermissions from '../../../hooks/usePermissions';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import CustomerInscriptionConfirmationDialog from './CustomerInscriptionConfirmationDialog';
import { CANCELLATION_REASONS, cancellationReasonOf, isCancellationChoice, learnerStatusLabel, saveCancellation } from '../../../utils/learnerStatus';

const ModernProgressBar = ({ value }) => (
  <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <LinearProgress 
      variant="determinate" 
      value={value} 
      sx={{ 
        flexGrow: 1,
        height: 8, 
        borderRadius: 4, 
        bgcolor: 'action.hover',
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
          background: value > 70 
            ? `linear-gradient(90deg, ${getTone('emerald').base} 0%, ${getTone('emerald').light} 100%)` 
            : `linear-gradient(90deg, ${getTone('indigo').base} 0%, ${getTone('indigo').light} 100%)`
        }
      }} 
    />
    <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', minWidth: '35px' }}>
      {value}%
    </Typography>
  </Box>
);

const StatusChip = ({ status, decalages, unsubscribedAt, unsubscribedComment }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case "Inscrit":
        return { color: getTone('emerald').base, bgcolor: toneAlpha('emerald', 0.1), label: "Inscrit" };
      case "En attente":
      case "À transférer":
        return { color: getTone('amber').base, bgcolor: toneAlpha('amber', 0.1), label: status };
      case "InscritD":
        return { color: getTone('indigo').base, bgcolor: toneAlpha('indigo', 0.1), label: "Inscrit D" };
      case "Décalé":
        return { color: getTone('violet').base, bgcolor: toneAlpha('violet', 0.1), label: "Décalé" };
      case "Désinscrit":
        return { color: getTone('rose').color, bgcolor: toneAlpha('rose', 0.12), label: "Désinscrit" };
      default:
        return { color: getTone('rose').base, bgcolor: toneAlpha('rose', 0.1), label: status };
    }
  };

  const config = getStatusConfig(status);
  const reason = cancellationReasonOf(status, unsubscribedComment);
  const tooltipTitle = (status === 'InscritD' || status === 'Décalé') && decalages
    ? decalages.map((dec) => `${dec.name} - Session ${dec.number_session}`).join('\n')
    : (status === 'Annulation' || status === 'À décaler' ? `Date : ${moment(unsubscribedAt).format('DD/MM/YYYY')}\nMotif : ${unsubscribedComment}` : null);

  const chip = (
    <Chip
      label={reason || config.label}
      size="small"
      sx={{
        bgcolor: config.bgcolor,
        color: config.color,
        fontWeight: 800,
        fontSize: "0.7rem",
        borderRadius: "8px",
        border: `1px solid ${config.color}20`,
        cursor: tooltipTitle ? 'help' : 'default'
      }}
    />
  );

  return tooltipTitle ? (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>} arrow placement="top">
      <span>{chip}</span>
    </Tooltip>
  ) : chip;
};

const CustomerFormationsListTable = (props) => {
  const { 
    formations = [], 
    setFormations, 
    formationsDec = [], 
    sources = [],
    getCustomer 
  } = props;
  const [showDec, setShowDec] = useState(false);
  const { user } = useAuth();
  const { can } = usePermissions();
  const [disableChoiceSession, setDisableChoiceSession] = useState(true);
  const [indexFormationDec, setIndexFormationDec] = useState(0);
  const [currentIdDec, setCurrentIdDec] = useState(0);
  const [valueIdSession, setValueIdSession] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteLearnerSessionId, setDeleteLearnerSessionId] = useState(-1);
  const [rows, setRows] = useState([]);
  const mounted = useMounted();
  const { customerId } = useParams();
  
  // Confirmation d'inscription : envoi manuel groupé, avec relecture.
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [activeDecalageStep, setActiveDecalageStep] = useState(0);
  const [selectedExistingFormation, setSelectedExistingFormation] = useState(null);
  const [sessionDecState, setSessionDecState] = useState(null);

  // Removed local getCustomer and useEffect as we use props

  const handleChange = async (params) => {
    let finalValue = params.value;
    if (params.field === 'primary_connection' || params.field === 'last_connection' || params.field === 'subscribed_at') {
      finalValue = moment(params.value).format('YYYY-MM-DD HH:mm:ss');
    }
    if (params.field === 'real_price') {
      finalValue = parseFloat(params.value).toFixed(2);
    }

    if (params.field === 'status' && isCancellationChoice(finalValue)) {
      const row = rows.find((r) => r.id === params.id);
      try {
        const patch = await saveCancellation(
          (...args) => sessionApi.editCustomer(...args),
          params.id,
          finalValue,
          row ? row.status : null,
          row ? row.unsubscribedComment : null,
          localStorage.getItem('accessToken')
        );
        setRows((prev) => prev.map((r) => (r.id === params.id
          ? { ...r, status: patch.status, ...(patch.unsubscribed_comment ? { unsubscribedComment: patch.unsubscribed_comment } : {}) }
          : r)));
        toast.success('Modification enregistrée');
      } catch (err) {
        console.error(err);
        toast.error('Erreur lors de la modification');
      }
      return;
    }

    if (finalValue === 'Décalé') {
      setShowDec(true);
      setCurrentIdDec(params.id);
      setActiveDecalageStep(0);
      setSelectedExistingFormation(null);
      setSessionDecState(null);
    } else {
      try {
        await sessionApi.editCustomer(2, params.id, finalValue, localStorage.getItem('accessToken'), params.field);
        toast.success('Modification enregistrée');
      } catch (err) {
        console.error(err);
        toast.error('Erreur lors de la modification');
      }
    }
  };

  const handleDeleteFormation = async (id) => {
    try {
      await customerApi.deleteCustomerFormation(id, localStorage.getItem('accessToken'));
      setFormations(prev => prev.filter(f => f.id !== id));
      toast.success('Formation supprimée');
      setOpenDelete(false);
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    setRows(formations.map((item) => ({ 
      id: item.id,
      name: item.name,
      progression: item.progression,
      status: item.status,
      startDate: item.start_date,
      endDate: item.end_date,
      firstTurnEndDate: item.first_turn_end_date,
      programNumber: item.programNumber,
      real_price: item.real_price,
      sourceFirstname: item.sourceFirstname,
      source_id: item.source_id,
      sourceLastname: item.sourceLastname,
      numberSession: item.number_session,
      lastConnection: item.last_connection,
      duration: item.duration,
      sessionId: item.session_id,
      decalages: item.decalages,
      totalProgress: item.totalProgress,
      subscribed_at: item.subscribed_at,
      unsubscribedAt: item.unsubscribed_at,
      unsubscribedComment: item.unsubscribed_comment
    })));
  }, [formations]);

  const handleDecalage = async () => {
    try {
      await sessionApi.decalage(currentIdDec, sessionDecState, localStorage.getItem('accessToken'));
      toast.success('Apprenant décalé !');
      setShowDec(false);
      getCustomer();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du décalage');
    }
  };

  const handleDecalageFromStatus = async () => {
    try {
      await sessionApi.decalageFromStatus(currentIdDec, selectedExistingFormation.id, localStorage.getItem('accessToken'));
      toast.success('Apprenant décalé !');
      setSelectedExistingFormation(null);
      setShowDec(false);
      getCustomer();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors du décalage');
    }
  };

  const columns = [
    { 
      field: 'name',
      headerName: 'Formation',
      flex: 1,
      minWidth: 350,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1, whiteSpace: 'normal', lineHeight: 1.2 }}>
          <Link
            component={RouterLink}
            to={`/dashboard/session/${params.row.sessionId}`}
            sx={{ 
              fontWeight: 800, 
              color: 'text.primary', 
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' }
            }}
          >
            {params.row.name}
          </Link>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
            Session #{params.row.numberSession} • N° du programme: {params.row.programNumber}
          </Typography>
        </Box>
      ) 
    },
    { 
      field: 'totalProgress', 
      headerName: 'Progression', 
      width: 150, 
      renderCell: (params) => <ModernProgressBar value={Number(parseInt(params.value || 0, 10))} /> 
    },
    { 
      field: 'status',
      headerName: 'Statut',
      width: 130,
      type: 'singleSelect',
      valueOptions: ['Inscrit', 'En attente', 'À décaler', 'Décalé', '- de 5 personnes', 'Annulation', ...CANCELLATION_REASONS, 'À transférer', 'Désinscrit'],
      // Une annulation motivée s'affiche et s'édite sous son motif.
      valueGetter: (params) => learnerStatusLabel(params.row.status, params.row.unsubscribedComment),
      editable: true,
      renderCell: (params) => (
        <StatusChip 
          status={params.row.status} 
          decalages={params.row.decalages}
          unsubscribedAt={params.row.unsubscribedAt}
          unsubscribedComment={params.row.unsubscribedComment}
        />
      )
    },
    { 
      field: 'subscribed_at', 
      headerName: 'Inscription', 
      width: 160, 
      type: 'dateTime',
      editable: can('sessions.learners.edit_subscription_date', [0, 4].includes(user.id_service)),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {params.value ? moment(params.value).format('DD/MM/YY HH:mm') : '-'}
        </Typography>
      ),
      renderEditCell: (params) => (
        <TextField
          type="datetime-local"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          value={params.value ? moment(params.value).format('YYYY-MM-DDTHH:mm') : ''}
          onChange={(e) => {
            const newValue = e.target.value ? moment(e.target.value).format('YYYY-MM-DD HH:mm:ss') : null;
            params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue }, e);
          }}
        />
      )
    },
    { 
      field: 'startDate', 
      headerName: 'Début', 
      width: 110, 
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {params.value ? moment(params.value).format('DD/MM/YY') : '-'}
        </Typography>
      ) 
    },
    { 
      field: 'firstTurnEndDate', 
      headerName: 'Fin Unité Clé', 
      width: 120, 
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {params.value ? moment(params.value).format('DD/MM/YY') : '-'}
        </Typography>
      ) 
    },
    { 
      field: 'endDate', 
      headerName: 'Fin', 
      width: 110, 
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {params.value ? moment(params.value).format('DD/MM/YY') : '-'}
        </Typography>
      ) 
    },
    { 
      field: 'lastConnection', 
      headerName: 'Dernière Conn.', 
      width: 160, 
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          {params.value ? moment(params.value).format('DD/MM/YY HH:mm') : '-'}
        </Typography>
      ) 
    },
    { 
      field: 'duration', 
      headerName: 'Durée', 
      width: 100, 
      editable: can('sessions.learners.edit_tracking', user.access_level === 0),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value || '-'}
        </Typography>
      ) 
    },
    { 
      field: 'real_price',
      headerName: 'Prix',
      width: 100,
      editable: can('sessions.learners.edit', user.access_level === 0 || user.id_service === 4),
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>
          {params.value} €
        </Typography>
      ) 
    },
    {
      field: 'source_id',
      headerName: 'Source',
      width: 150,
      type: 'singleSelect',
      valueOptions: sources.map((s) => s.id),
      valueFormatter: (params) => {
        const source = sources.find((s) => s.id === params.value);
        return source ? `${source.firstname} ${source.lastname}` : '';
      },
      editable: can('sessions.learners.edit', user.access_level === 0 || user.id_service === 4),
    },
    { 
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          disabled={!can('sessions.learners.remove', [0, 4].includes(user.id_service))}
          onClick={() => {
            setDeleteLearnerSessionId(params.row.id);
            setOpenDelete(true);
          }}
          sx={{ color: 'error.main', '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.1) } }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) 
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<MarkEmailReadIcon />}
          onClick={() => setShowConfirmation(true)}
          sx={{ borderRadius: '12px', fontWeight: 700, textTransform: 'none' }}
        >
          Confirmation d&apos;inscription
        </Button>
      </Box>

      <Card sx={{ borderRadius: '20px', border: (theme) => `1px solid ${theme.palette.divider}`, boxShadow: 'none', overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          rowHeight={85}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onCellEditCommit={handleChange}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 800, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }
            },
            '& .MuiDataGrid-cell': { borderBottom: (theme) => `1px solid ${theme.palette.background.default}` },
            '& .MuiDataGrid-footerContainer': { borderTop: (theme) => `1px solid ${theme.palette.divider}` }
          }}
          localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
        />
      </Card>

      <CustomerInscriptionConfirmationDialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        customerId={customerId}
        onSent={getCustomer}
      />

      {/* Decalage Dialog */}
      <Dialog open={showDec} onClose={() => setShowDec(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Processus de Décalage</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeDecalageStep} sx={{ py: 3 }}>
            <Step><StepLabel>Sélection</StepLabel></Step>
            <Step><StepLabel>Confirmation</StepLabel></Step>
          </Stepper>

          {activeDecalageStep === 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'text.secondary' }}>
                Formations existantes (Inscrit)
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {(() => {
                  const source = rows.find((r) => r.id === currentIdDec);
                  return formations
                    .filter((f) => f.status === 'Inscrit' && moment(f.end_date).year() >= moment().year() && f.session_id !== source?.sessionId)
                    .map((f) => (
                      <Grid item xs={12} sm={6} md={4} key={f.id}>
                        <Card
                          onClick={() => setSelectedExistingFormation(f)}
                          sx={{
                            p: 2,
                            cursor: 'pointer',
                            borderRadius: '16px',
                            border: selectedExistingFormation?.id === f.id ? `2px solid ${getTone('indigo').base}` : `1px solid ${grey(200)}`,
                            bgcolor: selectedExistingFormation?.id === f.id ? toneAlpha('indigo', 0.05) : 'white',
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: getTone('indigo').base }
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{f.name}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Session #{f.number_session}</Typography>
                        </Card>
                      </Grid>
                    ));
                })()}
              </Grid>

              <Divider sx={{ mb: 3 }}>
                <Chip label="OU NOUVELLE FORMATION" size="small" sx={{ fontWeight: 800, bgcolor: 'action.hover' }} />
              </Divider>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={formationsDec}
                    getOptionLabel={(o) => `${o.name} (${o.program_number})`}
                    onChange={(e, v) => {
                      setDisableChoiceSession(!v);
                      setIndexFormationDec(formationsDec.indexOf(v));
                      setSelectedExistingFormation(null);
                      setSessionDecState(null);
                      setValueIdSession(!valueIdSession);
                    }}
                    renderInput={(params) => <TextField {...params} label="Formation" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    key={valueIdSession}
                    options={formationsDec[indexFormationDec]?.sessions || []}
                    getOptionLabel={(o) => `#${o.number_session} (${moment(o.start_date).format('DD/MM/YY')})`}
                    onChange={(e, v) => {
                      setSessionDecState(v?.id);
                      setSelectedExistingFormation(null);
                    }}
                    disabled={disableChoiceSession}
                    renderInput={(params) => <TextField {...params} label="Session" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {activeDecalageStep === 1 && (
            <Box sx={{ mt: 4, p: 3, bgcolor: 'action.hover', borderRadius: '20px', border: (theme) => `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>Récapitulatif du transfert</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Source:</strong> {rows.find(r => r.id === currentIdDec)?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 800 }}>
                <strong>Cible:</strong> {selectedExistingFormation ? selectedExistingFormation.name : (formationsDec[indexFormationDec]?.name || 'Non sélectionnée')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowDec(false)} color="inherit">Annuler</Button>
          {activeDecalageStep === 0 ? (
            <Button 
              variant="contained" 
              onClick={() => (selectedExistingFormation || sessionDecState) ? setActiveDecalageStep(1) : toast.error('Sélectionnez une cible')}
              sx={{ borderRadius: '12px', px: 4 }}
            >
              Suivant
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => setActiveDecalageStep(0)}>Retour</Button>
              <Button variant="contained" onClick={selectedExistingFormation ? handleDecalageFromStatus : handleDecalage} sx={{ borderRadius: '12px', px: 4 }}>
                Confirmer le décalage
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), color: 'error.main', mx: 'auto', mb: 2, width: 56, height: 56 }}>
              <DeleteIcon />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Supprimer la formation ?</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Cette action retirera définitivement l'apprenant de cette session.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button onClick={() => setOpenDelete(false)} fullWidth sx={{ borderRadius: '12px', fontWeight: 700 }}>Annuler</Button>
          <Button onClick={() => handleDeleteFormation(deleteLearnerSessionId)} variant="contained" color="error" fullWidth sx={{ borderRadius: '12px', fontWeight: 800 }}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

CustomerFormationsListTable.propTypes = {
  formations: PropTypes.array,
  setFormations: PropTypes.func,
  formationsDec: PropTypes.array,
  sources: PropTypes.array
};

export default CustomerFormationsListTable;
